import { useEffect, useMemo } from "react"

import acc from "accounting"
import { Button, Divider, Flex, Typography } from "antd"
import { Trans, useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { NavLink } from "react-router-dom"

import Private from "../../../Components/Private"
import Icon from "../../../Icon"
import BalanceProgress from "./BalanceProgress"

import "../styles.less"
import "./BalanceBanner.less"

const { Text, Link } = Typography

const TopUpBalanceLink = ({ children }) => {
  return (
    <NavLink to="/settings/balance">
      <Link style={{ fontSize: 14 }}>{children}</Link>
    </NavLink>
  )
}

const BlockedHeader = () => {
  const { t } = useTranslation()
  return (
    <Flex gap={8} align="center" className="blocked-header">
      <Icon className="blocked-icon" type="off" />
      <Text type="danger">{t("Business accounts blocked")}</Text>
    </Flex>
  )
}

const NegativeHeader = () => {
  return (
    <Flex gap={8} align="center" className="negative-header">
      <Icon
        className="negative-header-icon"
        type="triangle_exclamation2"
        style={{ color: "#A1A1AA" }}
      />
    </Flex>
  )
}

const BlockedMessage = () => {
  const { t } = useTranslation()
  return (
    <Flex vertical gap={12}>
      <Text type="secondary" style={{ fontSize: 14, lineHeight: "20px" }}>
        <Trans
          i18nKey={
            "Your Business Accounts<1> are blocked due to low balance</1>. Please top up to continue."
          }
        >
          Your Business Accounts
          <Text type="danger"> are blocked due to low balance</Text>. Please top up to continue.
        </Trans>
      </Text>
      <NavLink to={"/settings/balance?open_add_funds=true"}>
        <Button className="balance-btn" size="small" type="primary">
          {t("Top up balance")}
        </Button>
      </NavLink>
    </Flex>
  )
}

// const NegativeBalanceMessage = ({ balanceText }) => {
//   const { t } = useTranslation();
//   return (
//     <Flex vertical gap={12}>
//       <Text type="secondary">
//         <Text>{t("Your balance is negative: ")}</Text>
//         <Text type="danger">{balanceText}</Text>.
//         <br />
//         {t("Your Business Accounts will be blocked.")}
//       </Text>
//       <NavLink to={"/settings/balance?open_add_funds=true"}>
//         <Button className="balance-btn" size="small" type="primary">
//           {t("Top up balance")}
//         </Button>
//       </NavLink>
//     </Flex>
//   );
// };

const MinimalZoneMessage = () => {
  return (
    <Text type="secondary" style={{ fontSize: 14, lineHeight: "20px" }}>
      <Trans i18nKey={"You are in a <1>minimum balance zone.</1>"}>
        You are in a <Text>minimum balance zone.</Text>
      </Trans>
      <br />
      <Trans i18nKey={"Please top up balance <1>here.</1>"}>
        Please top up balance<TopUpBalanceLink> here.</TopUpBalanceLink>
      </Trans>
    </Text>
  )
}

const DefaultMessage = () => {
  return (
    <Text type="secondary" style={{ fontSize: 14, lineHeight: "20px" }}>
      <Trans i18nKey={"You may top up the balance <1>here.</1>"}>
        You may top up balance <TopUpBalanceLink>here.</TopUpBalanceLink>
      </Trans>
    </Text>
  )
}

const BalanceBanner = ({
  isBlocked,
  min,
  recommended,
  current,
  updateBalance,
  isLoyaltyBilling
}) => {
  const { t } = useTranslation()
  const isMinBalanceZone = useMemo(() => current < min, [current, min])
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isNegative = useMemo(() => current < 0, [current])
  const balanceText = `${isNegative ? "-" : ""}$${acc.formatNumber(Math.abs(current), 2, ",", ".")}`

  const bannerOption = useMemo(() => {
    if (isLoyaltyBilling) return "default"
    if (isBlocked) return "blocked"
    if (isMinBalanceZone) return "minimal"
    return "default"
  }, [isBlocked, isMinBalanceZone, isLoyaltyBilling])

  useEffect(() => {
    updateBalance()
    const interval = setInterval(
      () => {
        updateBalance()
      },
      15 * 60 * 1000
    )
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <Flex vertical gap={8} className="additional-message-block">
        <Flex vertical gap={4}>
          {bannerOption === "blocked" && <BlockedHeader />}
          <Text type="secondary" style={{ fontSize: 14, lineHeight: "20px" }}>
            {t("Total balance")}
          </Text>
          <Text
            type={bannerOption === "minimal" || bannerOption === "blocked" ? "danger" : ""}
            style={{ fontSize: 14, lineHeight: "20px", fontWeight: 500 }}
          >
            <Private>{balanceText}</Private>
          </Text>
        </Flex>
        <BalanceProgress
          min={min}
          recommended={recommended}
          current={current}
          variant={isLoyaltyBilling ? "neutral" : "default"}
        />
        {bannerOption === "blocked" && <BlockedMessage />}
        {bannerOption === "default" && <DefaultMessage />}
        {bannerOption === "minimal" && <MinimalZoneMessage />}
      </Flex>
      {!isMobile && <Divider type={"horizontal"} style={{ margin: "16px auto 16px" }} />}
    </>
  )
}

export default BalanceBanner
